"use client";

import React from "react";
import { Building2, Layers, Wrench, Maximize2, ClipboardCheck, House as Home, Compass, Scale } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface DecisionItem {
  id: string;
  title: string;
  description: string;
}
const keyDecisions: DecisionItem[] = [{
  id: "open-concept",
  title: "Open Floor Plan Configuration",
  description: "Evaluating wall removals between kitchen, dining, and living areas to improve sightlines, natural light, and circulation flow."
}, {
  id: "room-reconfig",
  title: "Room Reconfiguration",
  description: "Relocating primary suites, expanding laundry utility zones, or shifting interior footprints to match current household routines."
}, {
  id: "systems-upgrades",
  title: "Systems Upgrades",
  description: "Planning modern electrical service expansions, multi-zone HVAC ductwork modifications, and updated plumbing distribution."
}, {
  id: "structural-work",
  title: "Structural Work",
  description: "Installing recessed steel or LVL load-bearing support beams, framing adjustments, and foundation verification for new openings."
}];
const costFactors: DecisionItem[] = [{
  id: "rooms-affected",
  title: "Number of Rooms Affected",
  description: "Every additional zone requires distinct trade coordination, protective dust containment, and multi-surface finish transitions."
}, {
  id: "structural-scope",
  title: "Structural Work Scope",
  description: "Removing load-bearing assemblies, modifying roof trusses, or adding foundation piers requires specialized engineering and framing labor."
}, {
  id: "systems-intensity",
  title: "Systems Upgrades",
  description: "The volume of new dedicated electrical circuits, replumbed fixture rough-ins, and ventilation paths directly determines trade hours."
}, {
  id: "square-footage",
  title: "Square Footage",
  description: "Overall floor area dictates material volumes, framing linear footage, subfloor leveling, drywall installation, and paint coats."
}, {
  id: "permits-inspections",
  title: "Permit & Inspection Complexity",
  description: "Municipal jurisdictional review schedules, historical overlays, and phased multi-trade code compliance sign-offs."
}];
export default function RemodelingDecisions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="remodelingdecisionsandcostfactors" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-mono uppercase tracking-wider text-xs border-primary/40 text-foreground">
            Project Planning & Architecture
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-tight">
            Key Planning Decisions & Cost Drivers
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Whole-home renovations involve interconnected architectural choices. Understanding structural decisions alongside physical scope variables helps establish clarity before construction begins.
          </p>
        </div>

        {/* Supporting Visual Feature Band */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <AspectRatio ratio={16 / 9} className="relative overflow-hidden bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_structural_beam_renovation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_structural_beam_renovation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_structural_beam_renovation.png-wvc-sizes" alt="Open concept interior remodeling with flush structural beam installation in a whole-home renovation" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
            </AspectRatio>
            <div className="p-4 sm:p-5 flex items-center justify-between border-t border-border">
              <span className="text-sm font-medium text-foreground flex items-center gap-2">
                <Compass className="size-4 text-primary" />
                Structural beam integration and interior boundary removals
              </span>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
            <AspectRatio ratio={16 / 9} className="relative overflow-hidden bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_reconfigured_living_space.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_reconfigured_living_space.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/whole_home_reconfigured_living_space.png-wvc-sizes" alt="Completed whole-home renovation displaying seamless wood flooring, upgraded electrical, and custom wall openings" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
            </AspectRatio>
            <div className="p-4 sm:p-5 flex items-center justify-between border-t border-border">
              <span className="text-sm font-medium text-foreground flex items-center gap-2">
                <Layers className="size-4 text-primary" />
                Continuous finish transitions across contiguous living zones
              </span>
            </div>
          </div>
        </div>

        {/* Two Core Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* List 1: Key Decisions */}
          <Card className="border border-border/80 shadow-md bg-card transition-all">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                  <Building2 className="size-5" />
                </div>
                <div>
                  <CardTitle className="font-serif text-2xl font-medium text-foreground">
                    Layout, Structural & Systems Decisions
                  </CardTitle>
                </div>
              </div>
              <CardDescription className="text-sm text-muted-foreground mt-1">
                Fundamental spatial choices that redefine how your home functions, circulates, and supports daily living.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <ul className="space-y-4" role="list">
                {keyDecisions.map((item, index) => <li key={item.id} data-index={index} className="p-4 rounded-lg bg-muted/30 border border-border/60 hover:border-primary/40 transition-colors">
                    <div className="flex items-start gap-3.5">
                      <div className="size-6 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-mono font-semibold">{index + 1}</span>
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-base font-semibold text-foreground tracking-tight">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </li>)}
              </ul>
            </CardContent>
          </Card>

          {/* List 2: Cost-Driving Factors */}
          <Card className="border border-border/80 shadow-md bg-card transition-all">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="size-10 rounded-lg bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary shrink-0">
                  <Scale className="size-5" />
                </div>
                <div>
                  <CardTitle className="font-serif text-2xl font-medium text-foreground">
                    Cost-Driving Factors
                  </CardTitle>
                </div>
              </div>
              <CardDescription className="text-sm text-muted-foreground mt-1">
                The objective scope dimensions and technical requirements that govern trade labor, equipment, and sequencing.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <ul className="space-y-4" role="list">
                {costFactors.map((item, index) => {
                const icons = [<Home key="1" className="size-4 text-foreground/70" />, <Wrench key="2" className="size-4 text-foreground/70" />, <Layers key="3" className="size-4 text-foreground/70" />, <Maximize2 key="4" className="size-4 text-foreground/70" />, <ClipboardCheck key="5" className="size-4 text-foreground/70" />];
                return <li key={item.id} data-index={index} className="p-4 rounded-lg bg-muted/30 border border-border/60 hover:border-secondary/40 transition-colors">
                      <div className="flex items-start gap-3.5">
                        <div className="size-6 rounded-full bg-secondary/15 text-secondary flex items-center justify-center shrink-0 mt-0.5">
                          {icons[index % icons.length]}
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-base font-semibold text-foreground tracking-tight">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </li>;
              })}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}